import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { AcademyAutocomplete } from '@/components/ui/AcademyAutocomplete';
import { useI18n } from '@/contexts/I18nContext';

interface SignUpStepTwoProps {
  formData: {
    email: string;
    password: string;
    confirmPassword: string;
    fullName: string;
    birthday: string;
    country: string;
    state: string;
    city: string;
    beltRank: string;
    isInstructor: boolean;
    username?: string;
    jiu_jitsu_school?: string;
    main_academy_id?: string;
  };
  onInputChange: (field: string, value: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  loading: boolean;
}

export const SignUpStepTwo = ({ formData, onInputChange, onSubmit, onBack, loading }: SignUpStepTwoProps) => {
  const { t } = useI18n();
  const [errors, setErrors] = useState({
    password: '',
    email: '',
  });
  const [openCountry, setOpenCountry] = useState(false);
  const [openState, setOpenState] = useState(false);
  const [openBeltRank, setOpenBeltRank] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const username = formData.username || '';
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'unavailable' | 'invalid'>('idle');
  const [usernameMessage, setUsernameMessage] = useState('');
  const [usernameTouched, setUsernameTouched] = useState(false);

  useEffect(() => {

  }, [formData.jiu_jitsu_school, formData.main_academy_id]);

  const validatePassword = (password: string) => {
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const isLongEnough = password.length >= 8;

    return hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && isLongEnough;
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateUsername = (value: string) => {
    return /^[a-z0-9]{5,}$/.test(value);
  };

  const checkUsernameAvailability = async (value: string) => {
    if (!validateUsername(value)) {
      setUsernameStatus('invalid');
      setUsernameMessage('Username must be at least 5 characters long and contain only lowercase letters and numbers.');
      return;
    }
    setUsernameStatus('checking');
    setUsernameMessage('Checking username...');
    const { data, error } = await supabase.rpc('check_username_available', { input_username: value });
    if (error) {
      setUsernameStatus('invalid');
              setUsernameMessage(t('auth.error_checking_username'));
      return;
    }
    if (data) {
      setUsernameStatus('available');
      setUsernameMessage('Username available');
    } else {
      setUsernameStatus('unavailable');
      setUsernameMessage('This username is not available, type another.');
    }
  };

  const handleInputChange = (field: string, value: string) => {
    onInputChange(field, value);
    
    // Clear error when user starts typing
    if (field === 'password') {
      setErrors(prev => ({ ...prev, password: '' }));
      if (value && !validatePassword(value)) {
        setErrors(prev => ({ 
          ...prev, 
          password: 'Minimum 8 characters, at least 1 lowercase, 1 uppercase, 1 number and 1 special character.'
        }));
      }
    }

    // Reset state and city when country changes
    if (field === 'country') {
      onInputChange('state', '');
      onInputChange('city', '');
      setTouched && setTouched(t => ({ ...t, state: false, city: false }));
    }

    // Reset city when state changes
    if (field === 'state') {
      onInputChange('city', '');
      setTouched && setTouched(t => ({ ...t, city: false }));
    }
  };

  const getStatesByCountry = (country: string) => {
    switch (country) {
      case 'brazil':
        return [
          { value: 'ac', label: 'Acre' },
          { value: 'al', label: 'Alagoas' },
          { value: 'ap', label: 'Amapá' },
          { value: 'am', label: 'Amazonas' },
          { value: 'ba', label: 'Bahia' },
          { value: 'ce', label: 'Ceará' },
          { value: 'df', label: 'Distrito Federal' },
          { value: 'es', label: 'Espírito Santo' },
          { value: 'go', label: 'Goiás' },
          { value: 'ma', label: 'Maranhão' },
          { value: 'mt', label: 'Mato Grosso' },
          { value: 'ms', label: 'Mato Grosso do Sul' },
          { value: 'mg', label: 'Minas Gerais' },
          { value: 'pa', label: 'Pará' },
          { value: 'pb', label: 'Paraíba' },
          { value: 'pr', label: 'Paraná' },
          { value: 'pe', label: 'Pernambuco' },
          { value: 'pi', label: 'Piauí' },
          { value: 'rj', label: 'Rio de Janeiro' },
          { value: 'rn', label: 'Rio Grande do Norte' },
          { value: 'rs', label: 'Rio Grande do Sul' },
          { value: 'ro', label: 'Rondônia' },
          { value: 'rr', label: 'Roraima' },
          { value: 'sc', label: 'Santa Catarina' },
          { value: 'sp', label: 'São Paulo' },
          { value: 'se', label: 'Sergipe' },
          { value: 'to', label: 'Tocantins' }
        ];
      case 'usa':
        return [
          { value: 'al', label: 'Alabama' },
          { value: 'ak', label: 'Alaska' },
          { value: 'az', label: 'Arizona' },
          { value: 'ar', label: 'Arkansas' },
          { value: 'ca', label: 'California' },
          { value: 'co', label: 'Colorado' },
          { value: 'ct', label: 'Connecticut' },
          { value: 'de', label: 'Delaware' },
          { value: 'fl', label: 'Florida' },
          { value: 'ga', label: 'Georgia' },
          { value: 'hi', label: 'Hawaii' },
          { value: 'id', label: 'Idaho' },
          { value: 'il', label: 'Illinois' },
          { value: 'in', label: 'Indiana' },
          { value: 'ia', label: 'Iowa' },
          { value: 'ks', label: 'Kansas' },
          { value: 'ky', label: 'Kentucky' },
          { value: 'la', label: 'Louisiana' },
          { value: 'me', label: 'Maine' },
          { value: 'md', label: 'Maryland' },
          { value: 'ma', label: 'Massachusetts' },
          { value: 'mi', label: 'Michigan' },
          { value: 'mn', label: 'Minnesota' },
          { value: 'ms', label: 'Mississippi' },
          { value: 'mo', label: 'Missouri' },
          { value: 'mt', label: 'Montana' },
          { value: 'ne', label: 'Nebraska' },
          { value: 'nv', label: 'Nevada' },
          { value: 'nh', label: 'New Hampshire' },
          { value: 'nj', label: 'New Jersey' },
          { value: 'nm', label: 'New Mexico' },
          { value: 'ny', label: 'New York' },
          { value: 'nc', label: 'North Carolina' },
          { value: 'nd', label: 'North Dakota' },
          { value: 'oh', label: 'Ohio' },
          { value: 'ok', label: 'Oklahoma' },
          { value: 'or', label: 'Oregon' },
          { value: 'pa', label: 'Pennsylvania' },
          { value: 'ri', label: 'Rhode Island' },
          { value: 'sc', label: 'South Carolina' },
          { value: 'sd', label: 'South Dakota' },
          { value: 'tn', label: 'Tennessee' },
          { value: 'tx', label: 'Texas' },
          { value: 'ut', label: 'Utah' },
          { value: 'vt', label: 'Vermont' },
          { value: 'va', label: 'Virginia' },
          { value: 'wa', label: 'Washington' },
          { value: 'wv', label: 'West Virginia' },
          { value: 'wi', label: 'Wisconsin' },
          { value: 'wy', label: 'Wyoming' }
        ];
      default:
        return [];
    }
  };

  const countries = [
    { value: 'afghanistan', label: 'Afghanistan' },
    { value: 'albania', label: 'Albania' },
    { value: 'algeria', label: 'Algeria' },
    { value: 'andorra', label: 'Andorra' },
    { value: 'angola', label: 'Angola' },
    { value: 'antigua-and-barbuda', label: 'Antigua and Barbuda' },
    { value: 'argentina', label: 'Argentina' },
    { value: 'armenia', label: 'Armenia' },
    { value: 'australia', label: 'Australia' },
    { value: 'austria', label: 'Austria' },
    { value: 'azerbaijan', label: 'Azerbaijan' },
    { value: 'bahamas', label: 'Bahamas' },
    { value: 'bahrain', label: 'Bahrain' },
    { value: 'bangladesh', label: 'Bangladesh' },
    { value: 'barbados', label: 'Barbados' },
    { value: 'belarus', label: 'Belarus' },
    { value: 'belgium', label: 'Belgium' },
    { value: 'belize', label: 'Belize' },
    { value: 'benin', label: 'Benin' },
    { value: 'bhutan', label: 'Bhutan' },
    { value: 'bolivia', label: 'Bolivia' },
    { value: 'bosnia-and-herzegovina', label: 'Bosnia and Herzegovina' },
    { value: 'botswana', label: 'Botswana' },
    { value: 'brazil', label: 'Brazil' },
    { value: 'brunei', label: 'Brunei' },
    { value: 'bulgaria', label: 'Bulgaria' },
    { value: 'burkina-faso', label: 'Burkina Faso' },
    { value: 'burundi', label: 'Burundi' },
    { value: 'cabo-verde', label: 'Cabo Verde' },
    { value: 'cambodia', label: 'Cambodia' },
    { value: 'cameroon', label: 'Cameroon' },
    { value: 'canada', label: 'Canada' },
    { value: 'central-african-republic', label: 'Central African Republic' },
    { value: 'chad', label: 'Chad' },
    { value: 'chile', label: 'Chile' },
    { value: 'china', label: 'China' },
    { value: 'colombia', label: 'Colombia' },
    { value: 'comoros', label: 'Comoros' },
    { value: 'congo', label: 'Congo' },
    { value: 'costa-rica', label: 'Costa Rica' },
    { value: 'croatia', label: 'Croatia' },
    { value: 'cuba', label: 'Cuba' },
    { value: 'cyprus', label: 'Cyprus' },
    { value: 'czech-republic', label: 'Czech Republic' },
    { value: 'democratic-republic-of-congo', label: 'Democratic Republic of Congo' },
    { value: 'denmark', label: 'Denmark' },
    { value: 'djibouti', label: 'Djibouti' },
    { value: 'dominica', label: 'Dominica' },
    { value: 'dominican-republic', label: 'Dominican Republic' },
    { value: 'ecuador', label: 'Ecuador' },
    { value: 'egypt', label: 'Egypt' },
    { value: 'el-salvador', label: 'El Salvador' },
    { value: 'equatorial-guinea', label: 'Equatorial Guinea' },
    { value: 'eritrea', label: 'Eritrea' },
    { value: 'estonia', label: 'Estonia' },
    { value: 'eswatini', label: 'Eswatini' },
    { value: 'ethiopia', label: 'Ethiopia' },
    { value: 'fiji', label: 'Fiji' },
    { value: 'finland', label: 'Finland' },
    { value: 'france', label: 'France' },
    { value: 'gabon', label: 'Gabon' },
    { value: 'gambia', label: 'Gambia' },
    { value: 'georgia', label: 'Georgia' },
    { value: 'germany', label: 'Germany' },
    { value: 'ghana', label: 'Ghana' },
    { value: 'greece', label: 'Greece' },
    { value: 'grenada', label: 'Grenada' },
    { value: 'guatemala', label: 'Guatemala' },
    { value: 'guinea', label: 'Guinea' },
    { value: 'guinea-bissau', label: 'Guinea-Bissau' },
    { value: 'guyana', label: 'Guyana' },
    { value: 'haiti', label: 'Haiti' },
    { value: 'honduras', label: 'Honduras' },
    { value: 'hungary', label: 'Hungary' },
    { value: 'iceland', label: 'Iceland' },
    { value: 'india', label: 'India' },
    { value: 'indonesia', label: 'Indonesia' },
    { value: 'iran', label: 'Iran' },
    { value: 'iraq', label: 'Iraq' },
    { value: 'ireland', label: 'Ireland' },
    { value: 'israel', label: 'Israel' },
    { value: 'italy', label: 'Italy' },
    { value: 'jamaica', label: 'Jamaica' },
    { value: 'japan', label: 'Japan' },
    { value: 'jordan', label: 'Jordan' },
    { value: 'kazakhstan', label: 'Kazakhstan' },
    { value: 'kenya', label: 'Kenya' },
    { value: 'kiribati', label: 'Kiribati' },
    { value: 'kuwait', label: 'Kuwait' },
    { value: 'kyrgyzstan', label: 'Kyrgyzstan' },
    { value: 'laos', label: 'Laos' },
    { value: 'latvia', label: 'Latvia' },
    { value: 'lebanon', label: 'Lebanon' },
    { value: 'lesotho', label: 'Lesotho' },
    { value: 'liberia', label: 'Liberia' },
    { value: 'libya', label: 'Libya' },
    { value: 'liechtenstein', label: 'Liechtenstein' },
    { value: 'lithuania', label: 'Lithuania' },
    { value: 'luxembourg', label: 'Luxembourg' },
    { value: 'madagascar', label: 'Madagascar' },
    { value: 'malawi', label: 'Malawi' },
    { value: 'malaysia', label: 'Malaysia' },
    { value: 'maldives', label: 'Maldives' },
    { value: 'mali', label: 'Mali' },
    { value: 'malta', label: 'Malta' },
    { value: 'marshall-islands', label: 'Marshall Islands' },
    { value: 'mauritania', label: 'Mauritania' },
    { value: 'mauritius', label: 'Mauritius' },
    { value: 'mexico', label: 'Mexico' },
    { value: 'micronesia', label: 'Micronesia' },
    { value: 'moldova', label: 'Moldova' },
    { value: 'monaco', label: 'Monaco' },
    { value: 'mongolia', label: 'Mongolia' },
    { value: 'montenegro', label: 'Montenegro' },
    { value: 'morocco', label: 'Morocco' },
    { value: 'mozambique', label: 'Mozambique' },
    { value: 'myanmar', label: 'Myanmar' },
    { value: 'namibia', label: 'Namibia' },
    { value: 'nauru', label: 'Nauru' },
    { value: 'nepal', label: 'Nepal' },
    { value: 'netherlands', label: 'Netherlands' },
    { value: 'new-zealand', label: 'New Zealand' },
    { value: 'nicaragua', label: 'Nicaragua' },
    { value: 'niger', label: 'Niger' },
    { value: 'nigeria', label: 'Nigeria' },
    { value: 'north-korea', label: 'North Korea' },
    { value: 'north-macedonia', label: 'North Macedonia' },
    { value: 'norway', label: 'Norway' },
    { value: 'oman', label: 'Oman' },
    { value: 'pakistan', label: 'Pakistan' },
    { value: 'palau', label: 'Palau' },
    { value: 'panama', label: 'Panama' },
    { value: 'papua-new-guinea', label: 'Papua New Guinea' },
    { value: 'paraguay', label: 'Paraguay' },
    { value: 'peru', label: 'Peru' },
    { value: 'philippines', label: 'Philippines' },
    { value: 'poland', label: 'Poland' },
    { value: 'portugal', label: 'Portugal' },
    { value: 'qatar', label: 'Qatar' },
    { value: 'romania', label: 'Romania' },
    { value: 'russia', label: 'Russia' },
    { value: 'rwanda', label: 'Rwanda' },
    { value: 'saint-kitts-and-nevis', label: 'Saint Kitts and Nevis' },
    { value: 'saint-lucia', label: 'Saint Lucia' },
    { value: 'saint-vincent-and-grenadines', label: 'Saint Vincent and Grenadines' },
    { value: 'samoa', label: 'Samoa' },
    { value: 'san-marino', label: 'San Marino' },
    { value: 'sao-tome-and-principe', label: 'Sao Tome and Principe' },
    { value: 'saudi-arabia', label: 'Saudi Arabia' },
    { value: 'senegal', label: 'Senegal' },
    { value: 'serbia', label: 'Serbia' },
    { value: 'seychelles', label: 'Seychelles' },
    { value: 'sierra-leone', label: 'Sierra Leone' },
    { value: 'singapore', label: 'Singapore' },
    { value: 'slovakia', label: 'Slovakia' },
    { value: 'slovenia', label: 'Slovenia' },
    { value: 'solomon-islands', label: 'Solomon Islands' },
    { value: 'somalia', label: 'Somalia' },
    { value: 'south-africa', label: 'South Africa' },
    { value: 'south-korea', label: 'South Korea' },
    { value: 'south-sudan', label: 'South Sudan' },
    { value: 'spain', label: 'Spain' },
    { value: 'sri-lanka', label: 'Sri Lanka' },
    { value: 'sudan', label: 'Sudan' },
    { value: 'suriname', label: 'Suriname' },
    { value: 'sweden', label: 'Sweden' },
    { value: 'switzerland', label: 'Switzerland' },
    { value: 'syria', label: 'Syria' },
    { value: 'taiwan', label: 'Taiwan' },
    { value: 'tajikistan', label: 'Tajikistan' },
    { value: 'tanzania', label: 'Tanzania' },
    { value: 'thailand', label: 'Thailand' },
    { value: 'timor-leste', label: 'Timor-Leste' },
    { value: 'togo', label: 'Togo' },
    { value: 'tonga', label: 'Tonga' },
    { value: 'trinidad-and-tobago', label: 'Trinidad and Tobago' },
    { value: 'tunisia', label: 'Tunisia' },
    { value: 'turkey', label: 'Turkey' },
    { value: 'turkmenistan', label: 'Turkmenistan' },
    { value: 'tuvalu', label: 'Tuvalu' },
    { value: 'uganda', label: 'Uganda' },
    { value: 'ukraine', label: 'Ukraine' },
    { value: 'united-arab-emirates', label: 'United Arab Emirates' },
    { value: 'united-kingdom', label: 'United Kingdom' },
    { value: 'usa', label: 'United States' },
    { value: 'uruguay', label: 'Uruguay' },
    { value: 'uzbekistan', label: 'Uzbekistan' },
    { value: 'vanuatu', label: 'Vanuatu' },
    { value: 'vatican-city', label: 'Vatican City' },
    { value: 'venezuela', label: 'Venezuela' },
    { value: 'vietnam', label: 'Vietnam' },
    { value: 'yemen', label: 'Yemen' },
    { value: 'zambia', label: 'Zambia' },
    { value: 'zimbabwe', label: 'Zimbabwe' }
  ];

  const getFieldError = (field: string) => {
    if (!formData[field] && touched[field]) {
      return t('auth.step_two.field_required');
    }
    if (field === 'email' && touched.email && formData.email && !validateEmail(formData.email)) {
      return t('auth.step_one.email_invalid');
    }
    return '';
  };

  const requiredFields = [
    'email', 'password', 'confirmPassword', 'fullName', 'birthday', 'country', 'beltRank', 'username'
  ];
  const hasEmptyRequired = requiredFields.some(field => !formData[field]);
  const isEmailInvalid = !formData.email || !validateEmail(formData.email);
  const isPasswordInvalid = !!errors.password || formData.password !== formData.confirmPassword;
  const isUsernameInvalid = !username || usernameStatus !== 'available';
  const isFormInvalid = hasEmptyRequired || isEmailInvalid || isPasswordInvalid || !formData.fullName || !formData.birthday || isUsernameInvalid;

  const showStateCityFields = formData.country === 'brazil' || formData.country === 'usa';

  return (
    <>
      <button
        onClick={onBack}
        type="button"
        className="flex items-center text-white/80 hover:text-white mb-4 text-sm"
      >
        ← {t('auth.step_one.back')}
      </button>
      <form onSubmit={(e) => {
        e.preventDefault();
        
        // Validate form before submitting
        if (isFormInvalid) {
          // Show specific validation errors
          if (!formData.email || !validateEmail(formData.email)) {
            setErrors(prev => ({ ...prev, email: t('auth.step_one.email_invalid') }));
            setEmailTouched(true);
          }
                     if (!formData.password || !validatePassword(formData.password)) {
             setErrors(prev => ({ ...prev, password: t('auth.step_two.password_requirements.characters') }));
           }
          if (formData.password !== formData.confirmPassword) {
            setErrors(prev => ({ ...prev, password: t('auth.step_two.passwords_not_match') }));
          }
          return;
        }
        
        onSubmit(e);
      }} className="space-y-8 bg-[#160C0C] py-8 px-4 rounded-2xl shadow-xl">
        <div className="space-y-2 mb-8">
          <div className="mb-6">
            <Input
              type="email"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={e => onInputChange('email', e.target.value)}
              onBlur={() => {
                setEmailTouched(true);
                if (!formData.email || !validateEmail(formData.email)) {
                  setErrors(prev => ({ ...prev, email: t('auth.step_one.email_invalid') }));
                } else {
                  setErrors(prev => ({ ...prev, email: '' }));
                }
              }}
              error={!!errors.email}
              placeholder={t('auth.email')}
              className="comment-selection"
            />
            {errors.email && emailTouched && (
              <p className="mt-0.5 text-sm text-white">{errors.email}</p>
            )}
          </div>
          <div className="mb-6">
            <label className="text-xs text-white">{t('auth.step_two.current_belt_rank')}</label>
            <Select
              value={formData.beltRank}
              onValueChange={value => onInputChange('beltRank', value)}
              open={openBeltRank}
              onOpenChange={setOpenBeltRank}
            >
              <SelectTrigger className="w-full relative [&_[data-radix-select-icon]]:hidden">
                <SelectValue placeholder={t('auth.step_two.select_belt_rank')} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  {openBeltRank ? (
                    <ChevronUp className="w-5 h-5 text-white" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-white" />
                  )}
                </span>
              </SelectTrigger>
              <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-black border-0 py-2 space-y-1">
                <SelectItem value="white" className="text-white truncate">{t('profile.promotions.white')}</SelectItem>
                <SelectItem value="blue" className="text-white truncate">{t('profile.promotions.blue')}</SelectItem>
                <SelectItem value="purple" className="text-white truncate">{t('profile.promotions.purple')}</SelectItem>
                <SelectItem value="brown" className="text-white truncate">{t('profile.promotions.brown')}</SelectItem>
                <SelectItem value="black" className="text-white truncate">{t('profile.promotions.black')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="!mb-6 !mt-6">
            <Input
              type="text"
              name="profile-name"
              autoComplete="off"
              value={username}
              onChange={e => {
                onInputChange('username', e.target.value);
                setUsernameTouched(true);
                setUsernameStatus('idle');
                setUsernameMessage('');
              }}
              onBlur={async () => {
                setUsernameTouched(true);
                await checkUsernameAvailability(username);
              }}
              placeholder={t('auth.step_two.choose_username')}
              required
              maxLength={32}
              className="bg-white/5 text-white placeholder:text-white/60 border-white font-sans comment-selection"
            />
            {usernameTouched && usernameStatus === 'invalid' && (
              <p className="mt-1 text-sm text-white/80">{usernameMessage}</p>
            )}
            {usernameTouched && usernameStatus === 'unavailable' && (
              <p className="mt-1 text-sm text-white/80">{usernameMessage}</p>
            )}
            {usernameTouched && usernameStatus === 'available' && (
              <p className="mt-1 text-sm text-white/80">{usernameMessage}</p>
            )}
            {usernameTouched && usernameStatus === 'checking' && (
              <p className="mt-1 text-sm text-white/80">{usernameMessage}</p>
            )}
          </div>

          {/* Campo de autocomplete de academia */}
          <div className="mb-6">
            <AcademyAutocomplete
              value={formData.jiu_jitsu_school ? { id: formData.main_academy_id || '', name: formData.jiu_jitsu_school } : ''}
              onChange={academy => {
                if (academy && typeof academy === 'object') {
                  onInputChange('main_academy_id', academy.id);
                  onInputChange('jiu_jitsu_school', academy.name);
                } else {
                  onInputChange('main_academy_id', '');
                  onInputChange('jiu_jitsu_school', academy || '');
                }
              }}
              placeholder={t('auth.step_two.search_academy')}
              className="mt-1 bg-transparent text-white placeholder:text-white/60 border-white font-sans"
            />
          </div>
        </div>
      
        <Input
          type="text"
          placeholder={t('auth.step_two.full_name_placeholder')}
          value={formData.fullName}
          onChange={(e) => onInputChange('fullName', e.target.value)}
          required
          onBlur={() => setTouched && setTouched(t => ({ ...t, fullName: true }))}
          className="comment-selection"
        />
        {(!formData.fullName && touched && touched.fullName) && (
          <p className="!mt-1 text-sm text-white/80">{t('auth.step_two.field_required')}</p>
        )}

        <Input
          type="date"
          placeholder={t('auth.step_two.birthday_placeholder')}
          value={formData.birthday}
          onChange={(e) => onInputChange('birthday', e.target.value)}
          required
          onBlur={() => setTouched && setTouched(t => ({ ...t, birthday: true }))}
          className="comment-selection"
        />
        {(!formData.birthday && touched && touched.birthday) && (
          <p className="!mt-1 text-sm text-white/80">{t('auth.step_two.field_required')}</p>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-xs text-white">{t('auth.step_two.country')}</label>
          <Select onValueChange={(value) => handleInputChange('country', value)} open={openCountry} onOpenChange={setOpenCountry}>
            <SelectTrigger className="relative [&_[data-radix-select-icon]]:hidden">
              <SelectValue placeholder={t('auth.step_two.country_placeholder')} />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                {openCountry ? (
                  <ChevronUp className="w-5 h-5 text-white" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-white" />
                )}
              </span>
          </SelectTrigger>
            <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-black border-0 py-2 space-y-1 max-h-60 overflow-y-auto">
              {countries.map((country) => (
                <SelectItem key={country.value} value={country.value} className="text-white truncate">
                  {country.label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        </div>

        {showStateCityFields && formData.country && (
          <div className="flex flex-col gap-1">
            <label className="text-xs text-white">{t('auth.step_two.state')}</label>
            <Select onValueChange={(value) => handleInputChange('state', value)} open={openState} onOpenChange={setOpenState}>
              <SelectTrigger className="relative [&_[data-radix-select-icon]]:hidden">
                <SelectValue placeholder={t('auth.step_two.state_placeholder')} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  {openState ? (
                    <ChevronUp className="w-5 h-5 text-white" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-white" />
                  )}
                </span>
          </SelectTrigger>
              <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-black border-0 py-2 space-y-1">
                {getStatesByCountry(formData.country).map((state) => (
                  <SelectItem key={state.value} value={state.value} className="text-white truncate">
                    {state.label}
                  </SelectItem>
                ))}
          </SelectContent>
        </Select>
          </div>
        )}

        {showStateCityFields && formData.state && (
          <Input
            type="text"
            placeholder={t('auth.step_two.city_placeholder')}
            value={formData.city}
            onChange={(e) => onInputChange('city', e.target.value)}
            required
            onBlur={() => setTouched(t => ({ ...t, city: true }))}
            className="comment-selection"
          />
        )}
        {getFieldError('city') && (
          <p className="!mt-0.5 text-sm text-white">{getFieldError('city')}</p>
        )}

        <div className="space-y-3">
          <label className="text-sm font-medium text-white">{t('auth.step_two.are_you')}</label>
          <RadioGroup
            value={formData.isInstructor ? 'instructor' : 'practitioner'}
            onValueChange={(value) => onInputChange('isInstructor', value === 'instructor')}
            className="flex flex-col space-y-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="instructor" id="instructor" className="border-white/60 data-[state=checked]:bg-[#D72E16] data-[state=checked]:border-[#D72E16]" />
              <label htmlFor="instructor" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white">
                {t('auth.step_two.instructor')}
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="practitioner" id="practitioner" className="border-white/60 data-[state=checked]:bg-[#D72E16] data-[state=checked]:border-[#D72E16]" />
              <label htmlFor="practitioner" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white">
                {t('auth.step_two.practitioner')}
              </label>
            </div>
          </RadioGroup>
        </div>

        <div className="relative">
          <PasswordInput
            name="password"
            autoComplete='new-password'
            placeholder={t('auth.step_two.password_placeholder')}
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            label={t('auth.step_two.password_label')}
            error={!!errors.password}
            required
            onBlur={() => setTouched(t => ({ ...t, password: true }))}
          />
          {getFieldError('password') && (
            <p className="mt-1 text-sm text-white">{getFieldError('password')}</p>
          )}
          {/* Requisitos de senha lado a lado */}
          <div className="flex flex-wrap gap-1 text-xs text-white/70 mt-2">
            <div className="flex items-center gap-1 px-2 py-1 bg-black/40 rounded">
              {formData.password.length >= 8 ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <X className="w-4 h-4 text-red-500" />
              )}
              <span>{t('auth.step_two.password_requirements.characters')}</span>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-black/40 rounded">
              {/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <X className="w-4 h-4 text-red-500" />
              )}
              <span>{t('auth.step_two.password_requirements.special_character')}</span>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-black/40 rounded">
              {/[A-Z]/.test(formData.password) ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <X className="w-4 h-4 text-red-500" />
              )}
              <span>{t('auth.step_two.password_requirements.uppercase')}</span>
        </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-black/40 rounded">
              {/[a-z]/.test(formData.password) ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <X className="w-4 h-4 text-red-500" />
              )}
              <span>{t('auth.step_two.password_requirements.lowercase')}</span>
          </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-black/40 rounded">
              {/[0-9]/.test(formData.password) ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <X className="w-4 h-4 text-red-500" />
              )}
              <span>{t('auth.step_two.password_requirements.number')}</span>
          </div>
          </div>
        </div>

        <div className="relative">
          <PasswordInput
            placeholder={t('auth.step_two.confirm_password_placeholder')}
            name="confirmPassword"
            autoComplete='new-password'
            value={formData.confirmPassword}
            onChange={(e) => onInputChange('confirmPassword', e.target.value)}
            label={t('auth.step_two.confirm_password_label')}
            required
            onBlur={() => setTouched(t => ({ ...t, confirmPassword: true }))}
          />
          {getFieldError('confirmPassword') && (
            <p className="mt-1 text-sm text-white">{getFieldError('confirmPassword')}</p>
          )}
          {formData.confirmPassword && formData.password !== formData.confirmPassword && (
            <p className="mt-1 text-sm text-white/80">{t('auth.step_two.passwords_not_match')}</p>
          )}
        </div>

        {/* Terms and Privacy Consent */}
        <div className="pt-4 pb-2">
          <p className="text-xs text-white/80 text-center leading-relaxed">
            {t('auth.step_two.consent_text')}{' '}
            <a 
              href="/terms-of-use" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#D72E16] hover:text-[#B8251A] underline"
            >
              {t('auth.step_two.terms_of_use')}
            </a>
            {' '}{t('auth.step_two.and')}{' '}
            <a 
              href="/privacy-policies" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#D72E16] hover:text-[#B8251A] underline"
            >
              {t('auth.step_two.privacy_policies')}
            </a>
            .
          </p>
        </div>

        <div className="flex pt-4">
          <Button
            type="submit"
            disabled={loading || isFormInvalid}
            className="flex-1 bg-[#D72E16] hover:bg-[#B8251A] text-white font-medium py-4 h-14 rounded-full transition-colors duration-200 disabled:opacity-50"
          >
            {loading ? t('auth.step_two.creating_account') : t('auth.step_two.create_account')}
          </Button>
        </div>
      </form>
    </>
  );
}; 